import { useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import ProductCard from "@/components/ProductCard";
import { useProducts } from "@/context/ProductsContext";

const TABS = [
  { key: "all", label: "Fragrances" },
  { key: "for-him", label: "For Him" },
  { key: "for-her", label: "For Her" },
  { key: "unisex", label: "Unisex" },
];

export default function ProductCarousel() {
  const { products } = useProducts();
  const [tab, setTab] = useState("all");
  const trackRef = useRef(null);

  const items = useMemo(() => {
    const list = tab === "all" ? products : products.filter((p) => p.collection === tab);
    return [...list].sort((a, b) => (b.bestseller ? 1 : 0) - (a.bestseller ? 1 : 0));
  }, [products, tab]);

  const scrollBy = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector("[data-carousel-item]");
    el.scrollBy({ left: dir * ((card?.clientWidth || 300) + 24), behavior: "smooth" });
  };

  return (
    <section data-testid="product-carousel-section" className="max-w-[1440px] mx-auto px-5 md:px-10 pt-20 md:pt-28 pb-10">
      <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
        <div className="flex items-center gap-6 md:gap-10 overflow-x-auto no-scrollbar" data-testid="product-carousel-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => { setTab(t.key); trackRef.current?.scrollTo({ left: 0 }); }}
              data-testid={`product-tab-${t.key}`}
              className={`relative whitespace-nowrap font-serif-brand text-2xl md:text-3xl font-light pb-2 transition-colors duration-300 ${tab === t.key ? "text-[#111]" : "text-black/35 hover:text-black/70"}`}
            >
              {t.label}
              <span className={`absolute left-0 bottom-0 h-px bg-[#BA8D53] transition-all duration-500 ${tab === t.key ? "w-full" : "w-0"}`} />
            </button>
          ))}
        </div>
        <div className="hidden md:flex items-center gap-2">
          <button type="button" onClick={() => scrollBy(-1)} data-testid="product-carousel-prev" aria-label="Previous" className="h-10 w-10 border border-black/20 flex items-center justify-center hover:bg-[#111] hover:text-[#F7F3EE] hover:border-[#111] transition-colors duration-300">
            <ChevronLeft size={16} strokeWidth={1.5} />
          </button>
          <button type="button" onClick={() => scrollBy(1)} data-testid="product-carousel-next" aria-label="Next" className="h-10 w-10 border border-black/20 flex items-center justify-center hover:bg-[#111] hover:text-[#F7F3EE] hover:border-[#111] transition-colors duration-300">
            <ChevronRight size={16} strokeWidth={1.5} />
          </button>
        </div>
      </Reveal>

      <div ref={trackRef} data-testid="product-carousel-track" className="flex gap-6 overflow-x-auto snap-x snap-mandatory no-scrollbar -mx-5 px-5 scroll-pl-5 md:mx-0 md:px-0 md:scroll-pl-0 pb-4">
        {items.map((p) => (
          <div key={`${tab}-${p.slug}`} data-carousel-item className="snap-start shrink-0 w-[78vw] sm:w-[44vw] md:w-[31%] lg:w-[23.5%]">
            <ProductCard p={p} />
          </div>
        ))}
        {items.length === 0 && <p className="text-black/50 py-10">New compositions arriving soon.</p>}
      </div>
    </section>
  );
}
